import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    CcReviewReviewHistory,
    CcReviewReviewHistoryCreate,
    CcReviewReviewTempAnswer,
} from "server/models"

import {
    APPROVED,
    OPEN,
    VERIFICATION,
    WORKFLOW_RERUN,
    VIEW,
} from "const"
import { CcStateSettingsType, hasProjectAccess } from "client/utils"
import {
    PRM_CC_DRAFTS,
    StoreType,
    setCcReviewReviewReloadAnswer,
    setCcReviewReviewTempAnswerHistory,
    initReviewReviewHistory,
} from "client/store"
import { PageForm } from "client/components"

import Form from "./form"

const Answer = () => {
    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewReview.revisionActiveList)
    const itemDetails = useSelector((state: StoreType) => state.ccReviewReview.itemDetails)
    const itemState = useSelector((state: StoreType) => state.ccReviewReview.itemState)
    const itemHistory = useSelector((state: StoreType) => state.ccReviewReview.itemHistory)
    const tempAnswerByUser = useSelector((state: StoreType) => state.ccReviewReview.tempAnswers)
    const reloadAnswer = useSelector((state: StoreType) => state.ccReviewReview.reloadAnswer)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[2] || [] // review level ids

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)

    const moduleResponseList = useSelector((state: StoreType) => state.ccResponse.moduleResponseList)
    const responseIds = moduleResponseList[3]
    const responsibleResponseIds = moduleResponseList[0]

    const userData = useSelector((state: StoreType) => state.system.userData)
    const dispatch = useDispatch()

    const [newHistoryCreate, setNewHistoryCreate] = useState<CcReviewReviewHistoryCreate>(null)

    const viewDraftsAccess = hasProjectAccess(PRM_CC_DRAFTS, VIEW)

    useEffect(() => {
        const { userId } = userData
        const { levelId, verifierId } = itemState || {}
        const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}
        const revision = revisionActiveList[itemDetails.id]
        const isFirstLevel = levelId === levelIds[0]

        let lastHistory: CcReviewReviewHistory = null
        let newHistory: CcReviewReviewHistoryCreate = null
        let selectedLevelId: number = null
        let selectedUserId: number = null
        let userIds: readonly number[] = []
        let groupId: number = null
        let tempAnswer: CcReviewReviewTempAnswer = null
        let tempAnswerHistory: CcReviewReviewHistory = null

        // get the last history row made by user, not by 'system'
        for (let i = itemHistory.length - 1; i >= 0; i--) {
            const history = itemHistory[i]
            if (history.workflow === WORKFLOW_RERUN) {
                break
            }
            if (history.systemMessage === false) {
                lastHistory = history
                break
            }
        }

        let nextHistoryRow = true
        if (lastHistory && lastHistory.nextStatus === APPROVED) nextHistoryRow = false
        if (nextHistoryRow) {
            if (levelId && verifierId === userId) {
                // define users on assigned approval level
                groupId = levelList[levelId] ? levelList[levelId].groupId : null
                userIds = (groupUserList && groupUserList[groupId]) || []
                tempAnswer = tempAnswerByUser[userId] || null

                // if current user is assigned to approval level
                if (userIds.includes(userId)) {
                    // define next approval level and users
                    for (let i = 0; i < levelIds.length; i++) {
                        if (levelIds[i] === levelId && levelIds[i + 1]) {
                            selectedLevelId = levelIds[i + 1]
                            break
                        }
                    }
                    groupId = levelList[selectedLevelId] ? levelList[selectedLevelId].groupId : null
                    userIds = (groupUserList && groupUserList[groupId]) || []

                    if (settings[selectedLevelId]) {
                        selectedUserId = settings[selectedLevelId]
                        if (!userIds.includes(selectedUserId)) selectedUserId = null
                    }

                    newHistory = {
                        itemId: itemDetails.id,
                        reviewReviewRevisionId: revision ? revision.id : null,
                        parentId: isFirstLevel ? null : (lastHistory && lastHistory.nextLevelId ? lastHistory.id : null),
                        prevLevelId: levelId,
                        prevUserId: userId,
                        nextLevelId: selectedLevelId,
                        nextUserId: tempAnswer ? tempAnswer.nextUserId : selectedUserId,
                        responseId: tempAnswer ? tempAnswer.responseId : (
                            isFirstLevel
                                ? (responsibleResponseIds && responsibleResponseIds[0]) || null
                                : (responseIds && responseIds[0]) || null
                        ),
                        remark: tempAnswer ? tempAnswer.remark : "",
                        nextRevision: tempAnswer ? tempAnswer.nextRevision : "",
                        prevStatus: isFirstLevel ? OPEN : VERIFICATION,
                        nextStatus: VERIFICATION,
                        workflow: null,
                        systemMessage: false,
                        editorId: null,
                    }
                }
            }

            // add tempAnswer to history
            if (
                levelId && verifierId
                && verifierId !== userId
                && tempAnswerByUser[verifierId]
                && viewDraftsAccess
            ) {
                tempAnswer = tempAnswerByUser[verifierId]
                // define next approval level
                for (let i = 0; i < levelIds.length; i++) {
                    if (levelIds[i] === levelId && levelIds[i + 1]) {
                        selectedLevelId = levelIds[i + 1]
                        break
                    }
                }

                tempAnswerHistory = {
                    ...initReviewReviewHistory,
                    id: -1,
                    parentId: isFirstLevel ? null : (lastHistory && lastHistory.nextLevelId ? lastHistory.id : null),
                    prevLevelId: levelId,
                    prevUserId: verifierId,
                    nextLevelId: selectedLevelId,
                    nextUserId: tempAnswer.nextUserId || null,
                    responseId: tempAnswer.responseId || null,
                    remark: tempAnswer.remark || "",
                    nextRevision: tempAnswer.nextRevision || "",
                    prevStatus: isFirstLevel ? OPEN : VERIFICATION,
                    nextStatus: VERIFICATION,
                    systemMessage: false,
                }
            }
        }

        setNewHistoryCreate(newHistory)
        dispatch(setCcReviewReviewTempAnswerHistory(tempAnswerHistory))

        dispatch(setCcReviewReviewReloadAnswer(false))
        setTimeout(() => {
            dispatch(setCcReviewReviewReloadAnswer(true))
        }, 0)
    }, [
        itemDetails,
        itemState,
        itemHistory,
        revisionActiveList,
        tempAnswerByUser,
        levelList,
        moduleLevelList,
        levelIds,
        responseIds,
        responsibleResponseIds,
    ])

    if (!newHistoryCreate) return null

    return (
        <PageForm noPaddingTop>
            {
                !reloadAnswer
                && <Form history={newHistoryCreate} />
            }
            {
                reloadAnswer
                && <Form history={newHistoryCreate} />
            }
        </PageForm>
    )
}
export default Answer
